import type { AdminFoodListItem } from '@steado/admin-contract/foods';

import type { TableColumn } from '@/components/table/DataTable';

/**
 * The catalog's optional columns.
 *
 * Every one is a field the list endpoint already returns, so turning one on
 * costs nothing but width — which is the whole reason they are optional rather
 * than always present: seven columns on a 1300px screen is a table nobody can
 * read, and which three matter depends on what the operator is curating today.
 */
export const OPTIONAL_FOOD_COLUMN_KEYS = ['mealRole', 'cuisines', 'portionCount'] as const;
export type OptionalFoodColumnKey = (typeof OPTIONAL_FOOD_COLUMN_KEYS)[number];

export const OPTIONAL_FOOD_COLUMNS: readonly (TableColumn & { key: OptionalFoodColumnKey })[] = [
  { key: 'mealRole', label: 'Meal role', width: '0.95fr' },
  { key: 'cuisines', label: 'Cuisine', width: '0.95fr' },
  { key: 'portionCount', label: 'Portions', width: '82px', align: 'right' },
];

/**
 * What each optional column shows, and whether it is showing an absence.
 *
 * `dim` marks a value that is the lack of a curation decision rather than a
 * decision: an uncurated meal role and a role of "staple" must not look alike,
 * because the first is work to do and the second is work done.
 */
export function optionalFoodCell(
  key: OptionalFoodColumnKey,
  food: AdminFoodListItem,
): { readonly value: string; readonly dim: boolean; readonly mono: boolean } {
  switch (key) {
    case 'mealRole':
      return {
        value: food.mealRole === null ? 'uncurated' : food.mealRole.replace(/_/g, ' '),
        dim: food.mealRole === null,
        mono: false,
      };
    case 'cuisines':
      return {
        value: food.cuisines.length === 0 ? 'unclassified' : food.cuisines.join(', ').replace(/_/g, ' '),
        dim: food.cuisines.length === 0,
        mono: false,
      };
    case 'portionCount':
      return {
        value: String(food.portionCount),
        dim: food.portionCount === 0,
        mono: true,
      };
  }
}
